import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { CalendarClock, Download } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { DataTable, optionalColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, MutedCell, NameCell } from '@/components/TableCells'
import type { ReportExecution } from '@/services/reports/reportsApi'
import { formatNumber } from '@/utils/format'
import { ReportStatusPill } from './ReportStatusPill'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  template: 'reportTemplate.name',
  status: 'status',
  runAt: 'executionStartTime',
  reportType: 'reportType',
}

interface ReportExecutionTableProps {
  executions: ReportExecution[]
  loading?: boolean
  emptyMessage?: string
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

/** Seconds the engine spent on a report, or nothing while it is still running. */
function duration(execution: ReportExecution): number | null {
  if (!execution.executionStartTime || !execution.executionEndTime) return null
  const ms =
    new Date(execution.executionEndTime).getTime() -
    new Date(execution.executionStartTime).getTime()
  return Number.isFinite(ms) && ms >= 0 ? Math.round(ms / 1000) : null
}

export function ReportExecutionTable({
  executions,
  loading,
  emptyMessage,
  sort,
  onSortChange,
}: ReportExecutionTableProps) {
  const { t } = useTranslation()

  const columns: ColumnDef<ReportExecution, unknown>[] = [
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 240 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      id: 'template',
      accessorFn: (row) => row.templateName,
      header: t('reports.template'),
      meta: { minWidth: 180 },
      cell: ({ row }) =>
        row.original.templateId ? (
          <Link
            to={`/reports/templates/${row.original.templateId}`}
            className="text-xs text-(--ad-link)"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.templateName || row.original.templateId}
          </Link>
        ) : (
          <MutedCell />
        ),
    },
    {
      accessorKey: 'status',
      header: t('reports.statusLabel'),
      size: 120,
      cell: ({ row }) => <ReportStatusPill status={row.original.status} />,
    },
    {
      id: 'runAt',
      accessorFn: (row) => row.executionStartTime || row.createTime,
      header: t('reportsModule.runAt'),
      size: 160,
      cell: ({ row }) => (
        <span className="flex items-center gap-1.5">
          <DateCell value={row.original.executionStartTime || row.original.createTime} withTime />
          {row.original.scheduleId && (
            <CalendarClock
              className="size-3 shrink-0 text-(--ad-text-faint)"
              aria-label={t('reportsModule.fromSchedule')}
            />
          )}
        </span>
      ),
    },
    {
      id: 'download',
      header: '',
      size: 56,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.result ? (
          <Button type="button" variant="ghost" size="icon" className="size-7" asChild>
            <a
              href={row.original.result}
              target="_blank"
              rel="noreferrer"
              title={t('reports.open')}
              aria-label={t('reports.open')}
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="size-4" />
            </a>
          </Button>
        ) : null,
    },
    optionalColumn<ReportExecution>({
      accessorKey: 'reportType',
      header: t('reportsModule.reportType'),
      size: 160,
      cell: ({ row }) => (
        <MutedCell
          value={
            row.original.reportType
              ? t(`reportTypes.${row.original.reportType}.simple`, {
                  defaultValue: row.original.reportType,
                })
              : ''
          }
        />
      ),
    }),
    optionalColumn<ReportExecution>({
      id: 'duration',
      accessorFn: (row) => duration(row) ?? -1,
      header: t('reportsModule.duration'),
      size: 110,
      cell: ({ row }) => {
        const seconds = duration(row.original)
        return (
          <MutedCell
            value={seconds === null ? '' : t('reportsModule.seconds', { count: seconds, value: formatNumber(seconds) })}
          />
        )
      },
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={executions}
      loading={loading}
      emptyMessage={emptyMessage ?? t('reportsModule.noReports')}
      columnsKey="reportExecutions"
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
